import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose } from '../_virtual/_rollupPluginBabelHelpers.js';
import { isNumber } from './assertion.esm.js';
import { isSSR } from './ssr.esm.js';

/**
 * Reduce a fraction
 * @param numerator
 * @param denominator
 */

function reduceFraction(numerator, denominator) {
  var gcd = function gcd(a, b) {
    return b ? gcd(b, a % b) : a;
  };

  var g = gcd(numerator, denominator);
  return [numerator / g, denominator / g];
}
/**
 * Returns a number whose value is limited to the given range.
 *
 * Example: limit the output of this computation to between 0 and 255
 * clamp((x * 255), 0, 255)
 *
 * @param input
 * @param min The lower boundary of the output range
 * @param max The upper boundary of the output range
 * @returns A number in the range [min, max]
 * @type Number
 */

function clamp(input, min, max) {
  if (input === void 0) {
    input = 0;
  }

  if (min === void 0) {
    min = 0;
  }

  if (max === void 0) {
    max = 255;
  }

  return Math.min(Math.max(input, min), max);
}
/**
 * Find the number closest to a target value
 * @param target The target value
 * @param values The range of values to check
 */

function closest(target, values) {
  var match = values.reduce(function (prev, value) {
    return Math.abs(value - target) < Math.abs(prev - target) ? value : prev;
  });
  return [values.indexOf(match), match];
}
/**
 * Get the number of decimal places
 * @param value
 */

function getDecimalPlaces(value) {
  var match = /(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/.exec(value.toString());

  if (!match) {
    return 0;
  }

  return Math.max(0, // Number of digits right of decimal point.
  (match[1] ? match[1].length : 0) - ( // Adjust for scientific notation.
  match[2] ? +match[2] : 0));
}
/**
 * Round to decimal places
 * @param input - the number to round
 * @param places - how many decimal places to round to
 */

function round(input, places) {
  return parseFloat(input.toFixed(places));
}
/**
 * Round to the nearest step
 * @param input - the number to round
 * @param step - the step to round to
 */

function roundToStep(input, step) {
  var places = getDecimalPlaces(step);
  return round(Math.round(input / step) * step, places);
}
/**
 * Format a number into a localised format
 *
 * @param input - Number to format
 * @param options - Formatting options
 */

function formatNumber(input, options) {
  var _ref = options != null ? options : {},
      _ref$neutral = _ref.neutral,
      neutral = _ref$neutral === void 0 ? true : _ref$neutral,
      _ref$language = _ref.language,
      language = _ref$language === void 0 ? !isSSR() ? navigator.language : 'en-US' : _ref$language,
      rest = _objectWithoutPropertiesLoose(_ref, ["neutral", "language"]);

  if (!isNumber(input)) {
    return input;
  }

  return new Intl.NumberFormat(neutral ? language.split('-')[0] : language, rest).format(input);
}

export { clamp, closest, formatNumber, getDecimalPlaces, reduceFraction, round, roundToStep };
//# sourceMappingURL=number.esm.js.map
